<!-- Header -->
<div class="d-flex flex-wrap flex-stack mb-6">
  <div>
    <h3 class="fw-bold my-2">{{ 'userEvents.title' | translate }}</h3>
    <p class="text-muted fs-6 mb-0">{{ 'userEvents.subtitle' | translate }}</p>
  </div>
</div>

<!-- Category Filter -->
@if (!loading && allEvents.length > 0) {
  <div class="filters-container mb-6">
    <div class="filter-group">
      <span class="filter-label">{{ 'userEvents.category' | translate }}</span>
      <div class="filter-buttons">
        <button
          class="filter-btn"
          [class.active]="categoryFilter === 'all'"
          (click)="categoryFilter = 'all'">
          {{ 'userEvents.filterAll' | translate }}
          <span class="filter-count">{{ allEvents.length }}</span>
        </button>
        <button
          class="filter-btn filter-competition"
          [class.active]="categoryFilter === 'competition'"
          (click)="categoryFilter = 'competition'">
          <i class="fa fa-trophy me-1"></i>
          {{ 'userEvents.filterCompetitions' | translate }}
          <span class="filter-count">{{ competitionCount }}</span>
        </button>
        <button
          class="filter-btn filter-training"
          [class.active]="categoryFilter === 'training'"
          (click)="categoryFilter = 'training'">
          <i class="fa fa-graduation-cap me-1"></i>
          {{ 'userEvents.filterTraining' | translate }}
          <span class="filter-count">{{ trainingCount }}</span>
        </button>
        <button
          class="filter-btn filter-social"
          [class.active]="categoryFilter === 'social'"
          (click)="categoryFilter = 'social'">
          <i class="fa fa-users me-1"></i>
          {{ 'userEvents.filterSocial' | translate }}
          <span class="filter-count">{{ socialCount }}</span>
        </button>
      </div>
    </div>
  </div>
}

<!-- Loading State -->
@if (loading) {
  <div class="card mb-9">
    <div class="card-body text-center py-15">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'userEvents.loading' | translate }}</span>
      </div>
      <p class="text-muted mt-5">{{ 'userEvents.loadingEvents' | translate }}</p>
    </div>
  </div>
}

<!-- Empty State - No events at all -->
@if (!loading && allEvents.length === 0) {
  <div class="card mb-9">
    <div class="empty-state">
      <div class="empty-state-icon">
        <i class="fa-solid fa-calendar-xmark"></i>
      </div>
      <h4>{{ 'userEvents.noEvents' | translate }}</h4>
      <p>{{ 'userEvents.noEventsHint' | translate }}</p>
      <app-button
        [variant]="'primary'"
        [label]="'userEvents.browseEvents' | translate"
        [iconClass]="'fa fa-search'"
        [routerLink]="'/shows'"
      />
    </div>
  </div>
}

<!-- Empty State - No events matching filter -->
@if (!loading && allEvents.length > 0 && filteredEvents.length === 0) {
  <div class="card mb-9">
    <div class="empty-state">
      <div class="empty-state-icon">
        <i class="fa-solid fa-filter"></i>
      </div>
      <h4>{{ 'userEvents.noMatchingEvents' | translate }}</h4>
      <p>{{ 'userEvents.noMatchingHint' | translate }}</p>
      <button class="btn btn-light-primary" (click)="categoryFilter = 'all'">
        <i class="fa fa-refresh me-2"></i>
        {{ 'userEvents.showAll' | translate }}
      </button>
    </div>
  </div>
}

<!-- Events Content -->
@if (!loading && filteredEvents.length > 0) {
  <!-- Upcoming Events Section -->
  @if (upcomingEvents.length > 0) {
    <div class="section-header mb-4">
      <h4 class="fw-bold mb-0">
        <i class="fa fa-calendar-check text-success me-2"></i>
        {{ 'userEvents.upcoming' | translate }}
        <span class="badge badge-success ms-2">{{ upcomingEvents.length }}</span>
      </h4>
    </div>
    <div class="row g-5 mb-9">
      @for (event of upcomingEvents; track event.event_id) {
        <div class="col-md-6 col-lg-4">
          <div class="card h-100 show-card">
            <!-- Event Image -->
            <div class="show-image-container position-relative">
              @if (event.header_url) {
                <img [src]="event.header_url" class="card-img-top show-image" [alt]="event.event_name"/>
              } @else {
                <div class="show-image-placeholder d-flex align-items-center justify-content-center">
                  <i class="fa fa-calendar fs-3x text-muted"></i>
                </div>
              }
              <!-- Category Badge -->
              <div class="position-absolute top-0 start-0 m-2">
                <span class="badge" [ngClass]="getCategoryBadgeClass(event)">
                  {{ getCategoryLabel(event) }}
                </span>
              </div>
            </div>

            <div class="card-body d-flex flex-column">
              <!-- Event Name -->
              <h5 class="card-title fw-bold mb-3">{{ event.event_name }}</h5>

              <!-- Event Details -->
              <div class="show-details mb-4">
                <!-- Date -->
                <div class="detail-row">
                  <i class="fa fa-calendar text-primary"></i>
                  <div>
                    <span class="fw-semibold">{{ formatDateDMYDots(event.start_date) }}</span>
                    @if (event.end_date && event.start_date !== event.end_date) {
                      <span class="text-muted"> - {{ formatDateDMYDots(event.end_date) }}</span>
                    }
                  </div>
                </div>

                <!-- Location -->
                <div class="detail-row">
                  <i class="fa fa-location-dot text-primary"></i>
                  <div>
                    <span class="fw-semibold">{{ event.city }}, {{ event.country }}</span>
                    @if (event.address) {
                      <a [href]="getGoogleMapsUrl(event)" target="_blank" class="d-block text-muted fs-8 text-decoration-none hover-primary">
                        {{ event.address }}
                        <i class="fa fa-external-link ms-1 fs-9"></i>
                      </a>
                    }
                  </div>
                </div>

                <!-- Organizer -->
                @if (event.company) {
                  <div class="detail-row">
                    <i class="fa fa-building text-primary"></i>
                    <span>{{ event.company.company_name }}</span>
                  </div>
                }
              </div>

              <!-- Action Button -->
              <div class="mt-auto">
                <app-button
                  [variant]="'primary'"
                  [label]="'userEvents.viewDetails' | translate"
                  [routerLink]="'/event-details/' + event.event_id"
                  class="w-100"
                />
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  }

  <!-- Past Events Section -->
  @if (pastEvents.length > 0) {
    <div class="section-header mb-4">
      <h4 class="fw-bold mb-0 text-muted">
        <i class="fa fa-history me-2"></i>
        {{ 'userEvents.past' | translate }}
        <span class="badge badge-secondary ms-2">{{ pastEvents.length }}</span>
      </h4>
    </div>
    <div class="row g-5 mb-9">
      @for (event of pastEvents; track event.event_id) {
        <div class="col-md-6 col-lg-4">
          <div class="card h-100 show-card past">
            <!-- Event Image -->
            <div class="show-image-container position-relative">
              @if (event.header_url) {
                <img [src]="event.header_url" class="card-img-top show-image" [alt]="event.event_name"/>
              } @else {
                <div class="show-image-placeholder d-flex align-items-center justify-content-center">
                  <i class="fa fa-calendar fs-3x text-muted"></i>
                </div>
              }
              <!-- Category Badge -->
              <div class="position-absolute top-0 start-0 m-2">
                <span class="badge" [ngClass]="getCategoryBadgeClass(event)">
                  {{ getCategoryLabel(event) }}
                </span>
              </div>
              <!-- Completed Badge -->
              <div class="position-absolute top-0 end-0 m-2">
                <span class="badge badge-secondary">{{ 'userEvents.completed' | translate }}</span>
              </div>
            </div>

            <div class="card-body d-flex flex-column">
              <!-- Event Name -->
              <h5 class="card-title fw-bold mb-3">{{ event.event_name }}</h5>

              <!-- Event Details -->
              <div class="show-details mb-4">
                <!-- Date -->
                <div class="detail-row">
                  <i class="fa fa-calendar text-muted"></i>
                  <div>
                    <span class="fw-semibold">{{ formatDateDMYDots(event.start_date) }}</span>
                    @if (event.end_date && event.start_date !== event.end_date) {
                      <span class="text-muted"> - {{ formatDateDMYDots(event.end_date) }}</span>
                    }
                  </div>
                </div>

                <!-- Location -->
                <div class="detail-row">
                  <i class="fa fa-location-dot text-muted"></i>
                  <span class="fw-semibold">{{ event.city }}, {{ event.country }}</span>
                </div>

                <!-- Organizer -->
                @if (event.company) {
                  <div class="detail-row">
                    <i class="fa fa-building text-muted"></i>
                    <span>{{ event.company.company_name }}</span>
                  </div>
                }
              </div>

              <!-- Action Button -->
              <div class="mt-auto">
                <app-button
                  [variant]="'outline'"
                  [label]="'userEvents.viewDetails' | translate"
                  [routerLink]="'/event-details/' + event.event_id"
                  class="w-100"
                />
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  }
}
